import React, { useMemo, useState } from "react";

/**
 * Paginated table used across the system — template markup, no DataTables lib.
 *
 * Props:
 *   columns:      [{ key, label, render?, className?, tdClassName?,
 *                    numeric?, sortable?, sortValue? }]
 *   data:         []
 *   loading:      bool
 *   pageSize:     initial rows per page (default 10)
 *   pageSizes:    selectable page sizes (default [10, 25, 50, 100])
 *   emptyMessage: shown when there is nothing to display
 *   onRowClick:   (row, i) => void — rows become clickable and show a chevron
 *   showRowNumbers: leading "#" column numbered across pages (default true)
 *
 * Column options:
 *   numeric    right-aligns and tabular-figures the column, for money/counts
 *   sortable   set false to opt a column out of sorting (default: sortable)
 *   sortValue  (row) => comparable — use when the rendered cell is not the
 *              value you want to sort on (formatted money, badges, dates)
 */
const DataTable = ({
  columns = [],
  data = [],
  onRowClick,
  rowDetailLabel = "View details",
  showRowNumbers = true,
  loading = false,
  pageSize = 10,
  pageSizes = [10, 25, 50, 100],
  emptyMessage = "No records found.",
  className = "",
  striped = true,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(pageSize);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const val = row[col.key];
        return val !== undefined && val !== null && String(val).toLowerCase().includes(q);
      })
    );
  }, [data, columns, search]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const valueOf = (row) => (col.sortValue ? col.sortValue(row) : row[col.key]);
    // Copy first — sorting in place would mutate the caller's array.
    return [...filtered].sort((a, b) => {
      const x = valueOf(a);
      const y = valueOf(b);
      // Blanks always sink, whichever direction is active.
      const xBlank = x === null || x === undefined || x === "";
      const yBlank = y === null || y === undefined || y === "";
      if (xBlank && yBlank) return 0;
      if (xBlank) return 1;
      if (yBlank) return -1;

      // Dates first. "2026-03-15" survives the digits-only strip as
      // "2026-03-15", which parseFloat reads as 2026 — so every date in a year
      // compared equal and date columns only ever sorted by year. ISO dates
      // already sort correctly as plain strings, so compare them that way.
      const isDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v.trim());
      if (isDate(x) && isDate(y)) {
        const cmp = String(x).localeCompare(String(y));
        return sort.dir === "asc" ? cmp : -cmp;
      }

      const nx = typeof x === "number" ? x : parseFloat(String(x).replace(/[^0-9.-]/g, ""));
      const ny = typeof y === "number" ? y : parseFloat(String(y).replace(/[^0-9.-]/g, ""));
      const numeric = col.numeric || (!Number.isNaN(nx) && !Number.isNaN(ny) && String(x).trim() !== "");
      const cmp = numeric && !Number.isNaN(nx) && !Number.isNaN(ny)
        ? nx - ny
        : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * perPage;
  const pageData = sorted.slice(startIdx, startIdx + perPage);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setCurrentPage(1);
    setSort((s) =>
      s.key !== col.key
        ? { key: col.key, dir: "asc" }
        : s.dir === "asc"
        ? { key: col.key, dir: "desc" }
        : { key: null, dir: "asc" } // third click clears the sort
    );
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    for (let i = Math.max(1, safeCurrentPage - delta); i <= Math.min(totalPages, safeCurrentPage + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  const headStyle = {
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  };

  if (loading) {
    return (
      <div>
        <div className="table-responsive">
          <table className={`table app-table text-nowrap mg-b-0 ${className}`}>
            <thead>
              <tr>
                {showRowNumbers && <th style={{ width: "52px" }}></th>}
                {columns.map((col, i) => <th key={i}><div style={{ height: "14px", background: "#e0e0e0", borderRadius: "3px" }}></div></th>)}
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, r) => (
                <tr key={r}>
                  {showRowNumbers && <td></td>}
                  {columns.map((_, c) => (
                    <td key={c}><div style={{ height: "12px", background: `rgba(224,224,224,${0.5 + (r % 2) * 0.2})`, borderRadius: "3px", animation: "shimmer 1.2s infinite" }}></div></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <style>{`@keyframes shimmer{0%{opacity:0.6}50%{opacity:1}100%{opacity:0.6}}`}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="tx-13 tx-muted">
            Showing {sorted.length === 0 ? 0 : startIdx + 1}–{Math.min(startIdx + perPage, sorted.length)} of {sorted.length} entries
            {search && data.length !== sorted.length && <span className="tx-muted"> (filtered from {data.length})</span>}
          </span>
          <select
            className="form-select form-select-sm no-print"
            aria-label="Rows per page"
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
            style={{ width: "auto" }}
          >
            {pageSizes.map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
        <div className="no-print">
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={search}
            onChange={handleSearch}
            style={{ width: "220px" }}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className={`table app-table text-nowrap mg-b-0 align-middle ${className}`}>
          <thead className="bg-light">
            <tr>
              {showRowNumbers && <th style={{ ...headStyle, width: "52px" }} className="text-end">#</th>}
              {columns.map((col, i) => {
                const sortable = col.sortable !== false;
                const active = sort.key === col.key;
                return (
                  <th
                    key={i}
                    className={`${col.className || ""} ${col.numeric ? "text-end" : ""}`}
                    style={{ ...headStyle, cursor: sortable ? "pointer" : "default", userSelect: "none" }}
                    onClick={() => toggleSort(col)}
                    aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                    title={sortable ? `Sort by ${col.label}` : undefined}
                  >
                    {col.label}
                    {sortable && (
                      <i
                        className={`ms-1 fa-solid ${
                          active ? (sort.dir === "asc" ? "fa-arrow-up-short-wide" : "fa-arrow-down-wide-short") : "fa-sort"
                        }`}
                        style={{ opacity: active ? 0.85 : 0.25, fontSize: "0.7rem" }}
                        aria-hidden="true"
                      ></i>
                    )}
                  </th>
                );
              })}
              {onRowClick && <th className="no-print" style={{ width: "34px" }}></th>}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (onRowClick ? 1 : 0) + (showRowNumbers ? 1 : 0)} className="text-center py-4 tx-muted">
                  <i className="fa-solid fa-inbox me-2" style={{ fontSize: "1.5rem", opacity: 0.4 }}></i>
                  <br />{emptyMessage}
                </td>
              </tr>
            ) : (
              pageData.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className={onRowClick ? "is-clickable" : undefined}
                  onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
                  title={onRowClick ? rowDetailLabel : undefined}
                >
                  {showRowNumbers && (
                    // Continues across pages, so the number is the record's
                    // position in the whole result set, not just on this page.
                    <td className="text-end tx-muted row-number">{startIdx + rowIdx + 1}</td>
                  )}
                  {columns.map((col, colIdx) => (
                    <td
                      key={colIdx}
                      className={`${col.tdClassName || ""} ${col.numeric ? "text-end" : ""}`}
                      style={col.numeric ? { fontVariantNumeric: "tabular-nums" } : undefined}
                    >
                      {col.render ? col.render(row[col.key], row, rowIdx) : (row[col.key] ?? "—")}
                    </td>
                  ))}
                  {onRowClick && (
                    <td className="text-end row-go no-print" aria-hidden="true">
                      <i className="fa-solid fa-chevron-right"></i>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <span className="tx-12 tx-muted">Page {safeCurrentPage} of {totalPages}</span>
          <nav className="no-print">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${safeCurrentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} aria-label="First page">«</button>
              </li>
              <li className={`page-item ${safeCurrentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(safeCurrentPage - 1)} aria-label="Previous page">‹</button>
              </li>
              {getPageNumbers().map((p) => (
                <li key={p} className={`page-item ${p === safeCurrentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                </li>
              ))}
              <li className={`page-item ${safeCurrentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(safeCurrentPage + 1)} aria-label="Next page">›</button>
              </li>
              <li className={`page-item ${safeCurrentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(totalPages)} aria-label="Last page">»</button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default DataTable;
